import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { LayoutDashboard, User, Heart, HeartHandshake, BookOpen, FolderKanban, ScrollText, Sparkles, Command as CommandIcon, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Entry {
  to: string;
  label: string;
  hint: string;
  icon: React.ReactNode;
  keywords: string;
}

const entries: Entry[] = [
  { to: "/schools",           label: "Principal Overview",   hint: "Executive dashboard",           icon: <LayoutDashboard className="w-4 h-4" />, keywords: "1 principal executive kpi hdi" },
  { to: "/schools/student",   label: "Student Profile",      hint: "Aarav Sharma · Grade 10",       icon: <User className="w-4 h-4" />,            keywords: "2 student aarav learner profile" },
  { to: "/schools/parent",    label: "Parent Dashboard",     hint: "Family engagement",             icon: <Heart className="w-4 h-4" />,           keywords: "3 parent family conversation" },
  { to: "/schools/counsellor",label: "Counsellor Dashboard", hint: "Prioritised caseload",          icon: <HeartHandshake className="w-4 h-4" />,  keywords: "4 counsellor caseload interventions" },
  { to: "/schools/studios",   label: "Learning Studios",     hint: "WALS · WAFLE · WAFFS",          icon: <BookOpen className="w-4 h-4" />,        keywords: "5 studios wals wafle waffs modules" },
  { to: "/schools/portfolio", label: "Portfolio",            hint: "Digital learner profiles",      icon: <FolderKanban className="w-4 h-4" />,    keywords: "6 portfolio artifacts evidence" },
  { to: "/schools/script",    label: "Demo Script",          hint: "Presenter cheat sheet · print", icon: <ScrollText className="w-4 h-4" />,      keywords: "7 script cheat sheet print presenter" },
  { to: "/schools/boardpack", label: "Board Pack",           hint: "One-page board snapshot · PDF", icon: <FileText className="w-4 h-4" />,        keywords: "8 boardpack board pdf print snapshot governors" },
];

export default function SchoolsCommand() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      // Number-key quick-jump (1-8) — only when the palette is closed and the
      // user isn't typing into an input. Uses Alt as a safety modifier so
      // number keys inside pages (filters, sliders) keep working.
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (!typing && e.altKey && !e.metaKey && !e.ctrlKey && /^[1-8]$/.test(e.key)) {
        const target = entries[parseInt(e.key, 10) - 1];
        if (target) {
          e.preventDefault();
          navigate(target.to);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);


  const go = useMemo(
    () => (to: string) => {
      setOpen(false);
      navigate(to);
    },
    [navigate]
  );

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="hidden md:inline-flex gap-2 text-muted-foreground"
        onClick={() => setOpen(true)}
        aria-label="Open demo quick-jump"
      >
        <CommandIcon className="w-3.5 h-3.5" />
        <span>Jump…</span>
        <kbd className="ml-1 hidden lg:inline-flex items-center gap-0.5 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">
          ⌘K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Jump to a demo screen…" />
        <CommandList>
          <CommandEmpty>No screen matches.</CommandEmpty>
          <CommandGroup heading="Demo flow">
            {entries.slice(0, 6).map((e, i) => (
              <CommandItem key={e.to} value={`${e.label} ${e.keywords}`} onSelect={() => go(e.to)}>
                {e.icon}
                <span className="ml-2">{e.label}</span>
                <span className="ml-2 text-xs text-muted-foreground">{e.hint}</span>
                <CommandShortcut>⌥{i + 1}</CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />
          <CommandGroup heading="Presenter tools">
            {entries.slice(6).map((e) => (
              <CommandItem key={e.to} value={`${e.label} ${e.keywords}`} onSelect={() => go(e.to)}>
                {e.icon}
                <span className="ml-2">{e.label}</span>
                <span className="ml-2 text-xs text-muted-foreground">{e.hint}</span>
              </CommandItem>
            ))}
            <CommandItem value="print current" onSelect={() => { setOpen(false); window.print(); }}>
              <Sparkles className="w-4 h-4" />
              <span className="ml-2">Print current screen</span>
              <CommandShortcut>⌘P</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
